import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Form, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import socketService from '../../services/socket';
import { getMyCoinBalanceApi, getCoinPlansApi, getCoinPlanByIdApi } from '../../api/coins';
import './PracticeDashboard.scss';

// Temporary Mock Data for Languages since API is not fully provided
const MOCK_LANGUAGES = [
  { id: '1', name: 'English' },
  { id: '2', name: 'Spanish' },
  { id: '3', name: 'French' },
  { id: '4', name: 'German' },
  { id: '5', name: 'Chinese' },
];

export const PracticeDashboard = () => {
  const navigate = useNavigate();
  const user = useSelector(state => state.auth?.user || JSON.parse(localStorage.getItem('user') || '{}'));
  
  const [isDND, setIsDND] = useState(false);
  const [onlineCount, setOnlineCount] = useState(1);
  const [coins, setCoins] = useState(0);
  const [minutes, setMinutes] = useState(0);
  const [plans, setPlans] = useState([]);
  const [specificPlan, setSpecificPlan] = useState(null);
  
  const [selectedLanguage, setSelectedLanguage] = useState(MOCK_LANGUAGES[0].id);
  const [selectedLevel, setSelectedLevel] = useState('Intermediate');
  
  const levels = [
    { id: 'Beginner', icon: 'bi-leaf' },
    { id: 'Intermediate', icon: 'bi-bar-chart-fill' },
    { id: 'Advanced', icon: 'bi-rocket-takeoff-fill' },
    { id: 'Native', icon: 'bi-award-fill' }
  ];

  useEffect(() => {
    // Initialize socket connection
    const socket = socketService.connect();
    
    // Listen for authenticated user data
    socket.on('authenticated', (data) => {
      setIsDND(data.isDND);
      setCoins(data.coin_balance || data.remaining_coins);
      setMinutes(data.practice_minutes_remaining || data.remaining_minutes);
    });

    // Listen for online count
    socket.on('onlineCount', (count) => {
      setOnlineCount(count);
    });

    // Listen for DND updates
    socket.on('dndStatusUpdated', (data) => {
      setIsDND(data.isDND);
    });
    
    // Listen for search starting
    socket.on('searchingStarted', (data) => {
      if (data.status === 'success') {
        navigate('/practice/matching', { 
          state: { 
            languageId: selectedLanguage,
            level: selectedLevel 
          }
        });
      }
    });

    // Additional Socket Listeners
    socket.on('userStatusChanged', (data) => console.log('Socket [userStatusChanged]:', data));
    socket.on('notify_all_users_result', (data) => console.log('Socket [notify_all_users_result]:', data));
    socket.on('notificationReceived', (data) => console.log('Socket [notificationReceived]:', data));
    socket.on('practice_language_updated', (data) => console.log('Socket [practice_language_updated]:', data));
    socket.on('practice_language_changed', (data) => console.log('Socket [practice_language_changed]:', data));
    socket.on('practice_language_data', (data) => console.log('Socket [practice_language_data]:', data));
    socket.on('offlineSuccess', (data) => console.log('Socket [offlineSuccess]:', data));
    socket.on('searching', (data) => console.log('Socket [searching]:', data));
    socket.on('matchCancelled', (data) => console.log('Socket [matchCancelled]:', data));
    socket.on('searchCancelled', (data) => console.log('Socket [searchCancelled]:', data));
    socket.on('toggleDNDResponse', (data) => console.log('Socket [toggleDNDResponse]:', data));
    socket.on('matchSkipped', (data) => console.log('Socket [matchSkipped]:', data));
    socket.on('skipConfirmed', (data) => console.log('Socket [skipConfirmed]:', data));
    socket.on('deductMinute', (data) => {
      console.log('Socket [deductMinute]:', data);
      if (data?.practice_minutes_remaining !== undefined) setMinutes(data.practice_minutes_remaining);
    });
    socket.on('coinsData', (data) => console.log('Socket [coinsData]:', data));
    socket.on('insufficientCoins', (data) => console.log('Socket [insufficientCoins]:', data));
    socket.on('coinsDeducted', (data) => console.log('Socket [coinsDeducted]:', data));
    socket.on('coinBalanceUpdated', (data) => {
      console.log('Socket [coinBalanceUpdated]:', data);
      if (data?.coin_balance !== undefined) setCoins(data.coin_balance);
    });

    // Fetch Initial Coin Balance and Plans
    const fetchCoinData = async () => {
      try {
        const balanceRes = await getMyCoinBalanceApi();
        if (balanceRes?.status && balanceRes?.data) {
          setCoins(balanceRes.data.coin_balance || 0);
          setMinutes(balanceRes.data.practice_minutes_remaining || 0);
        }

        const plansRes = await getCoinPlansApi();
        if (plansRes?.status && plansRes?.data) {
          setPlans(plansRes.data);
        }

        // Specific plan requested by user
        try {
          const specificPlanRes = await getCoinPlanByIdApi('6a103bcc9a961b6e39ce4039');
          if (specificPlanRes?.status && specificPlanRes?.data) {
            setSpecificPlan(specificPlanRes.data);
          }
        } catch (e) {
          console.warn('Specific plan fetch error (might not exist):', e);
        }
      } catch (error) {
        console.error('Error fetching coin data:', error);
      }
    };

    fetchCoinData();

    return () => {
      socket.off('authenticated');
      socket.off('onlineCount');
      socket.off('dndStatusUpdated');
      socket.off('searchingStarted');
      socket.off('userStatusChanged');
      socket.off('notify_all_users_result');
      socket.off('notificationReceived');
      socket.off('practice_language_updated');
      socket.off('practice_language_changed');
      socket.off('practice_language_data');
      socket.off('offlineSuccess');
      socket.off('searching');
      socket.off('matchCancelled');
      socket.off('searchCancelled');
      socket.off('toggleDNDResponse');
      socket.off('matchSkipped');
      socket.off('skipConfirmed');
      socket.off('deductMinute');
      socket.off('coinsData');
      socket.off('insufficientCoins');
      socket.off('coinsDeducted');
      socket.off('coinBalanceUpdated');
    };
  }, [navigate, selectedLanguage, selectedLevel]);

  const toggleDND = () => {
    const newStatus = !isDND;
    setIsDND(newStatus);
    const socket = socketService.getSocket();
    if (socket) {
      socket.emit('toggleDND', { userId: user?._id, isDND: newStatus });
    }
  };

  const handleFindPartner = () => {
    const socket = socketService.getSocket();
    if (socket && user?._id) {
      socket.emit('startSearch', {
        userId: user._id,
        languageId: selectedLanguage,
        level: selectedLevel
      });
    } else {
      // If no socket, just simulate navigation
      navigate('/practice/matching', { 
        state: { languageId: selectedLanguage, level: selectedLevel }
      });
    }
  };

  const handleAIPractice = () => {
    navigate('/practice/ai-buddy');
  };

  // --- Supplementary Socket Emit Functions ---
  const emitSetPracticeLanguage = (langId) => socketService.getSocket()?.emit('set_practice_language', { userId: user?._id, languageId: langId });
  const emitGetPracticeLanguage = () => socketService.getSocket()?.emit('get_practice_language', user?._id);
  const emitGoOffline = () => socketService.getSocket()?.emit('goOffline', user?._id);
  const emitCancelSearch = () => socketService.getSocket()?.emit('cancelSearch', user?._id);
  const emitRespondToRequest = (matchId, response) => socketService.getSocket()?.emit('respondToRequest', { matchId, response, userId: user?._id });
  const emitDeductMinute = () => socketService.getSocket()?.emit('deductMinute', { userId: user?._id });
  const emitGetCoins = () => socketService.getSocket()?.emit('getCoins', { userId: user?._id });
  const emitDeductCoins = (deductAmount) => socketService.getSocket()?.emit('deductCoins', { userId: user?._id, coins: deductAmount });


  return (
    <div className="practice-dashboard bg-white min-vh-100 py-4">
      <Container className="d-flex flex-column gap-4 max-w-1000">
        
        {/* Top Stats Banner */}
        <div className="pd-stats-banner d-flex align-items-center justify-content-between p-3 rounded-4 bg-light shadow-sm">
          
          <div className="d-flex align-items-center gap-4 flex-grow-1 justify-content-center">
            <div className="d-flex align-items-center gap-2">
              <div className="bg-white rounded-circle p-1 d-flex align-items-center justify-content-center shadow-sm" style={{ width: '40px', height: '40px' }}>
                <i className="bi bi-coin text-warning fs-5"></i>
              </div>
              <div>
                <h5 className="mb-0 fw-bold text-dark">{coins || 7000}</h5>
                <small className="text-muted">Coins</small>
              </div>
            </div>

            <div style={{ height: '30px', width: '1px', backgroundColor: '#e9ecef' }}></div>

            <div className="d-flex align-items-center gap-2">
              <div className="bg-white rounded-circle p-1 d-flex align-items-center justify-content-center shadow-sm" style={{ width: '40px', height: '40px' }}>
                <i className="bi bi-droplet-fill text-primary fs-5"></i>
              </div>
              <div>
                <h5 className="mb-0 fw-bold text-dark">{minutes || 1001}</h5>
                <small className="text-muted">Minutes</small>
              </div>
            </div>
          </div>
        </div>

        {/* Status / DND Header */}
        <div className="d-flex align-items-center justify-content-between mt-2">
          <div className="d-flex align-items-center gap-2 bg-success bg-opacity-10 text-success px-3 py-1 rounded-pill fw-semibold small">
            <span className="bg-success rounded-circle" style={{ width: '8px', height: '8px' }}></span>
            Online ({onlineCount})
          </div>
          <button 
            className={`btn rounded-pill px-4 fw-semibold border ${isDND ? 'btn-dark' : 'btn-white shadow-sm'}`}
            onClick={toggleDND}
          >
            <i className={`bi ${isDND ? 'bi-bell-slash-fill' : 'bi-bell-slash'} me-2`}></i>
            DND {isDND ? 'ON' : 'OFF'}
          </button>
        </div>

        {/* Hero Section */}
        <div className="pd-hero py-4 text-center text-md-start d-flex flex-column flex-md-row align-items-center justify-content-between">
          <div className="mb-4 mb-md-0">
            <h1 className="fw-bolder mb-2" style={{ color: '#1a1d20' }}>
              Find a practice partner<br/>
              <span className="text-primary">right now!</span>
            </h1>
            <p className="text-muted fs-5 mb-0" style={{ maxWidth: '350px' }}>
              We'll match you with someone online who wants to practice.
            </p>
          </div>
          <div className="pd-hero-illustration">
            {/* Using a pure CSS/Icon mockup for the illustration from the screenshot */}
            <div className="d-flex gap-4">
              <div className="pd-person-icon blue-person">
                <i className="bi bi-person-fill"></i>
                <div className="pd-bubble"><i className="bi bi-chat-dots-fill"></i></div>
              </div>
              <div className="pd-person-icon yellow-person mt-4">
                <i className="bi bi-person-fill"></i>
                <div className="pd-bubble"><i className="bi bi-chat-dots-fill"></i></div>
              </div>
            </div>
          </div>
        </div>

        {/* Matching Controls */}
        <Card className="border-0 shadow-sm rounded-4 bg-white p-4 mt-2">
          <Row className="g-4 align-items-end">
            <Col lg={3} md={6}>
              <Form.Group>
                <Form.Label className="small text-muted fw-semibold">I want to practice</Form.Label>
                <Form.Select 
                  className="rounded-3 py-2 bg-light border-0"
                  value={selectedLanguage}
                  onChange={(e) => setSelectedLanguage(e.target.value)}
                >
                  {MOCK_LANGUAGES.map(lang => (
                    <option key={lang.id} value={lang.id}>{lang.name}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            
            <Col lg={5} md={6}>
              <Form.Label className="small text-muted fw-semibold">Level I'm looking for</Form.Label>
              <div className="d-flex gap-2">
                {levels.map(lvl => (
                  <button
                    key={lvl.id}
                    className={`pd-level-btn ${selectedLevel === lvl.id ? 'active' : ''}`}
                    onClick={() => setSelectedLevel(lvl.id)}
                  >
                    <i className={`bi ${lvl.icon} fs-5 mb-1`}></i>
                    <span style={{ fontSize: '0.7rem' }}>{lvl.id}</span>
                  </button>
                ))}
              </div>
            </Col>
            
            <Col lg={4} className="d-flex gap-2">
              <Button 
                variant="primary" 
                className="flex-grow-1 rounded-3 py-2 fw-semibold d-flex align-items-center justify-content-center gap-2"
                style={{ backgroundColor: '#1d2a9f', borderColor: '#1d2a9f' }}
                onClick={handleFindPartner}
              >
                <i className="bi bi-lightning-charge-fill"></i> Find Partner
              </Button>
              <Button 
                variant="dark" 
                className="flex-grow-1 rounded-3 py-2 fw-semibold d-flex align-items-center justify-content-center gap-2"
                style={{ backgroundColor: '#13113c' }}
                onClick={handleAIPractice}
              >
                <i className="bi bi-lightbulb-fill"></i> AI Practice
              </Button>
            </Col>
          </Row>
        </Card>

        {/* Promo Banner */}
        <div className="pd-promo-banner mt-3 p-4 rounded-4 d-flex flex-column flex-md-row align-items-center justify-content-between">
          <div className="d-flex align-items-center gap-3 mb-3 mb-md-0">
            <div className="bg-warning rounded-circle d-flex align-items-center justify-content-center text-white shadow-sm" style={{ width: '60px', height: '60px' }}>
              <i className="bi bi-lightning-charge-fill fs-2"></i>
            </div>
            <div>
              <h5 className="fw-bold mb-1">Get more minutes</h5>
              <p className="text-muted mb-0 small">Practice more, improve faster!</p>
            </div>
          </div>
          
          <div className="bg-white rounded-3 p-2 px-3 d-flex align-items-center gap-3 shadow-sm">
            <div className="bg-danger rounded-3 text-white d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
              <i className="bi bi-lightning-fill"></i>
            </div>
            <div>
              <h6 className="mb-0 fw-bold small">Pocket Toons</h6>
              <small className="text-muted" style={{ fontSize: '0.65rem' }}>App Store</small>
            </div>
            <Button variant="primary" size="sm" className="rounded-pill px-3 ms-2 fw-bold">Get</Button>
          </div>
        </div>

      </Container>
    </div>
  );
};
